import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { familyHubs, noteHubs, type DiscoveryCounts, type DiscoveryKind } from '../../../../packages/shared/src/discovery.js';
import { useResource } from '../api.js';

const notes = [...noteHubs].sort();
const families = Object.keys(familyHubs);
const title = (slug: string) => slug.charAt(0).toUpperCase() + slug.slice(1);

function IndexRow({ kind, slug, count }: { kind: DiscoveryKind; slug: string; count?: number }) {
  return (
    <li>
      <Link className="scent-index-row" to={'/finder?' + (kind === 'notes' ? 'notes=' : 'families=') + encodeURIComponent(slug)} aria-label={title(slug) + (count ? ', ' + count + ' fragrances' : '')}>
        <span className="scent-index-name">{title(slug)}</span>
        {count ? <span className="scent-index-count">{count}</span> : null}
      </Link>
    </li>
  );
}

/** Homepage discovery: an index of note hubs beside a plate of scent families that ends with the finder. */
export function ScentIndex({ initialCounts }: { initialCounts?: DiscoveryCounts }) {
  const counts = useResource<DiscoveryCounts>('/api/v1/discovery-counts', initialCounts && { url: '/api/v1/discovery-counts', data: initialCounts }).data;
  // Hubs with no matching fragrances would open an empty page, so the index leaves them out once counts are known.
  const shownNotes = counts ? notes.filter(slug => counts.notes[slug] > 0) : notes;
  const shownFamilies = counts ? families.filter(slug => counts.families[slug] > 0) : families;
  // Two roomier columns while the list is short; three once it grows past eight notes.
  const wide = shownNotes.length > 8;
  const columns = { '--rows': Math.ceil(shownNotes.length / (wide ? 3 : 2)), '--rows-narrow': Math.ceil(shownNotes.length / 2) } as CSSProperties;
  return (
    <section className="home-discovery scent-index" aria-labelledby="home-discovery-heading">
      <header className="scent-index-heading">
        <h2 id="home-discovery-heading">Find your scent</h2>
        <p>Start with a note you enjoy or explore a broader scent family.</p>
      </header>
      <div className="scent-index-body">
        <div className={'scent-index-notes' + (wide ? ' is-wide' : '')}>
          <div className="scent-index-ledger-head">
            <h3>By note</h3>
            {counts && <span>Fragrances in the collection</span>}
          </div>
          <ul style={columns}>{shownNotes.map(slug => <IndexRow key={slug} kind="notes" slug={slug} count={counts?.notes[slug]} />)}</ul>
        </div>
        <aside className="scent-index-families" aria-labelledby="scent-index-families-heading">
          <h3 id="scent-index-families-heading">By scent family</h3>
          <ul>{shownFamilies.map(slug => <IndexRow key={slug} kind="families" slug={slug} count={counts?.families[slug]} />)}</ul>
          <div className="scent-index-finder">
            <p>Start with a scent profile, then refine the families and notes you enjoy.</p>
            <Link className="button" to="/finder">Find by scent</Link>
          </div>
        </aside>
      </div>
    </section>
  );
}
